import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

// Lazy one-time loader (memoized)
// Use the import map alias so the path stays dynamic per store/theme.
const SWATCHES_MODULE_URL = "@theme/media-gallery";

// Global memoization so it survives quick-add re-inits, theme editor section reloads, etc.
const loadSwatchesModule = (() => {
  return () => {
    // If the swatches element is already registered, don't import again.
    // Update the tag name below if your module defines a different one.
    if (customElements.get("product-color-swatches")) {
      return Promise.resolve();
    }

    // Global promise cache
    window.__selfieSwatchesModulePromise ||= import(SWATCHES_MODULE_URL).catch((err) => {
      window.__selfieSwatchesModulePromise = null;
      throw err;
    });

    return window.__selfieSwatchesModulePromise;
  };
})();


export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  // NEW: temporary overrides when opening from external sources (e.g., Searchspring)
  /** @type {string | null} */
  #overrideUrl = null;
  /** @type {string | null} */
  #overrideVariantId = null;

  /**
   * Open the Quick Add for an arbitrary product URL/variant (e.g., from Searchspring).
   * This uses your existing modal + product form flow so cart events/drawer behave normally.
   * @param {{ productUrl: string, variantId?: string, productTitle?: string }} opts
   */
  async openWith(opts) {
    const { productUrl, variantId, productTitle } = opts || {};
    if (!productUrl) return;

    // Set temporary overrides for this open cycle
    this.#overrideUrl = productUrl || null;
    this.#overrideVariantId = variantId || null;
    if (productTitle) this.dataset.productTitle = productTitle;

    this.#openQuickAddModal();
    this.#setModalLoading(true);

    try {
      const html = await this.fetchProductPage(this.productPageUrl);
      const gridEl = html?.querySelector('[data-product-grid-content]');
      if (gridEl) {
        const fresh = /** @type {Element} */ (gridEl.cloneNode(true));
        await this.updateQuickAddModal(fresh);
      }
      this.#setModalLoading(false);
    } finally {
      // Clear overrides so normal behavior resumes for native quick-add buttons
      this.#overrideUrl = null;
      this.#overrideVariantId = null;
    }
  }

  get productPageUrl() {
    // Prefer override URL (and merge the override variant) when set via openWith()
    if (this.#overrideUrl) {
      const url = new URL(this.#overrideUrl, window.location.origin);
      if (this.#overrideVariantId) url.searchParams.set('variant', this.#overrideVariantId);
      return url.toString();
    }

    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();


    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();
    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Handles native quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    this.#openQuickAddModal();
    this.#setModalLoading(true);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
    }

    this.#setModalLoading(false);
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    
      // refresh wishlist buttons after quick add HTML loads
      clearTimeout(this._swymRefresh);
      this._swymRefresh = setTimeout(() => {
        window.SwymCallbacks = window.SwymCallbacks || [];
        window.SwymCallbacks.push((swat) => {
          swat.initializeActionButtons('body');
        });
      }, 50);
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productMedia = productGrid.querySelector('.product-information__media');
      productMedia?.remove();

      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);
    this.#setModalLoading(false);
    this.#syncVariantSelection(modalContent);

    if (modalContent.querySelector('product-color-swatches')) {
      await loadSwatchesModule();
      // Force attribute update so Lit sees a change even if morph re-used the node
      const src = productGrid.querySelector('product-color-swatches');
      const dst = modalContent.querySelector('product-color-swatches');
      const newProps = src?.getAttribute('props');
      if (dst && newProps) dst.setAttribute('props', newProps); // triggers updated()
    }

    // Re-run promo/price wiring inside the modal if available
    window.refreshPromoPricing?.(modalContent);
  }

  #setModalLoading(isLoading) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    if (isLoading) {
      if (!modalContent.hasAttribute('data-loading')) {
        modalContent.setAttribute('data-loading', 'true');
        modalContent.innerHTML =
          '<div class="quick-add-modal__loading" role="status" aria-live="polite">' +
          '<span class="quick-add-modal__loading-spinner" aria-hidden="true"></span>' +
          '<span class="visually-hidden">Loading product details</span>' +
          '</div>';
      }
      return;
    }

    modalContent.removeAttribute('data-loading');
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection into the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    // Prefer an override variant if we were opened via openWith()
    const selectedVariantId = this.#overrideVariantId || this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(ThemeEvents.variantUpdate, this.#updateWishlistButtons);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };
  
  #queueSwymRefresh() {
    clearTimeout(this._swymRefresh);
  
    this._swymRefresh = setTimeout(() => {
      window.SwymCallbacks = window.SwymCallbacks || [];
      window.SwymCallbacks.push((swat) => {
        swat.initializeActionButtons('body');
      });
    }, 50);
  }
  
  #updateWishlistButtons = (/** @type {CustomEvent} */ event) => {
    const variant = event.detail?.resource;
    if (!variant?.id) return;
  
    const scope = this.querySelector('#quick-add-modal-content') || this;
    const buttons = scope.querySelectorAll('.BIS_trigger, .pdp-wishlist');
    if (!buttons.length) return;
  
    const productId = String(variant.product_id ?? '');
    const variantId = String(variant.id);
  
    let didUpdate = false;
  
    buttons.forEach((btn) => {
      if (!(btn instanceof HTMLElement)) return;
  
      if (productId && btn.dataset.productId !== productId) {
        btn.dataset.productId = productId;
        didUpdate = true;
      }
  
      if (btn.dataset.variantId !== variantId) {
        btn.dataset.variantId = variantId;
        didUpdate = true;
      }
    });
  
    if (didUpdate) {
      this.#queueSwymRefresh();
    }
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * Patch for iOS 16.0 UI freeze on dialog close.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
